import type { ComparisonPreferencesState } from '../../redux/features/comparisonPreferencesSlice';

/** The nine match factors the profile's importance UI lets a user weigh, one per scoring weight. */
export type MatchFactor =
  | 'title'
  | 'roleLevel'
  | 'location'
  | 'jobType'
  | 'salary'
  | 'vacation'
  | 'benefits'
  | 'remoteness'
  | 'languages';

export const MATCH_FACTORS: MatchFactor[] = [
  'title',
  'roleLevel',
  'location',
  'jobType',
  'salary',
  'vacation',
  'benefits',
  'remoteness',
  'languages',
];

export type ImportanceLevel = 'less' | 'normal' | 'more';

export type ImportanceMap = Record<MatchFactor, ImportanceLevel>;

/** Points assigned to each importance level before normalizing into a weight. */
const IMPORTANCE_POINTS: Record<ImportanceLevel, number> = {
  less: 1,
  normal: 2,
  more: 3,
};

/** Maps each match factor to the ComparisonPreferencesState weight field it drives. */
const WEIGHT_FIELD_FOR_FACTOR: Record<MatchFactor, keyof ComparisonPreferencesState> = {
  title: 'titleWeight',
  roleLevel: 'roleLevelWeight',
  location: 'locationWeight',
  jobType: 'jobTypeWeight',
  salary: 'salaryWeight',
  vacation: 'vacationWeight',
  benefits: 'benefitsWeight',
  remoteness: 'remotenessWeight',
  languages: 'languagesWeight',
};

/** Defaults per the profile redesign spec: salary/location/remoteness weigh most, languages/vacation/benefits weigh least. */
export const DEFAULT_IMPORTANCE: ImportanceMap = {
  salary: 'more',
  location: 'more',
  remoteness: 'more',
  title: 'normal',
  roleLevel: 'normal',
  jobType: 'normal',
  languages: 'less',
  vacation: 'less',
  benefits: 'less',
};

/**
 * Rewrites all nine comparison weights from an importance map: less = 1 point, normal = 2,
 * more = 3, each factor's weight = its points / the sum of all nine factors' points. The
 * resulting weights always sum to 1 (modulo floating point), so every existing consumer of
 * `ComparisonPreferencesState` (match scoring, fit ledger, etc.) keeps working unchanged.
 */
export function deriveWeightsFromImportance(importance: ImportanceMap): ComparisonPreferencesState {
  const totalPoints = MATCH_FACTORS.reduce((sum, factor) => sum + IMPORTANCE_POINTS[importance[factor]], 0);

  const weights = {} as ComparisonPreferencesState;
  for (const factor of MATCH_FACTORS) {
    const field = WEIGHT_FIELD_FOR_FACTOR[factor];
    weights[field] = totalPoints > 0 ? IMPORTANCE_POINTS[importance[factor]] / totalPoints : 1 / MATCH_FACTORS.length;
  }
  return weights;
}

/**
 * Migration for rehydrated state that predates the importance map: buckets each factor's
 * existing weight relative to the mean of all nine weights (which is always ~1/9 since they
 * sum to 1). Below the mean = 'less', above 1.3x the mean = 'more', otherwise 'normal'.
 */
export function deriveImportanceFromWeights(weights: ComparisonPreferencesState): ImportanceMap {
  const values = MATCH_FACTORS.map((factor) => weights[WEIGHT_FIELD_FOR_FACTOR[factor]]);
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;

  // Floating point epsilon so values that are equal to the mean up to rounding noise (e.g. nine
  // weights that are each exactly 1/9 but don't sum to a bit-exact 1) land on 'normal' instead
  // of flipping to 'less' from a sub-percent rounding difference.
  const epsilon = mean * 1e-9;

  const result = {} as ImportanceMap;
  for (const factor of MATCH_FACTORS) {
    const value = weights[WEIGHT_FIELD_FOR_FACTOR[factor]];
    if (value < mean - epsilon) {
      result[factor] = 'less';
    } else if (value > mean * 1.3) {
      result[factor] = 'more';
    } else {
      result[factor] = 'normal';
    }
  }
  return result;
}
